import {
  type MutationState,
  useMutation,
  useMutationState
} from "@tanstack/react-query";
import { useMemo } from "react";

import { useDeviceData } from "~/providers/app";
import { getAuth, isProfileAuth } from "~/providers/store/auth";
import { useKioskStore } from "~/routes/__app.kiosk/route";
import { useKioskShopperName } from "~/routes/__app.kiosk/use-kiosk-shopper-name";
import { useKioskProducts } from "~/stores/kiosk-session/hooks/use-kiosk-products";
import {
  useKioskDiningOption,
  useKioskSession
} from "~/stores/kiosk-session/hooks/use-kiosk-session";
import { type MutationType } from "~/utils/tanstack-query";
import { useVoidPoiPayment } from "~/hooks/freedompay/use-void-poi-payment";
import { logger } from "~/services/logger";
import { kioskUsesCustomShopperName } from "~/utils/kiosk-requires-order-instructions";
import { getKioskUserIdentifier } from "~/utils/tills/get-kiosk-user-identifier";
import { useCurrentSchool } from "~/hooks/queries/schools";
import { minorUnitToMajorUnit } from "@repo/system";
import { POI_TIMEOUT_MS } from "~/hooks/freedompay/constants";
import { type TransactionItem } from "~/types/events";
import {
  useLatestStartPoiPayment,
  useStartPoiPayment
} from "~/hooks/freedompay/use-start-poi-payment";
import { useLatestMutation } from "~/utils/tanstack-query";
import {
  DisplayError,
  DisplayErrorCodeEnum,
  isDisplayError
} from "~/utils/display-error";

import {
  type AcknowledgeOrderPaymentV4Args,
  createOrderV1,
  createOrderV3,
  type CreateOrderV3Args,
  acknowledgeOrderPaymentV4,
  type CreateOrderV4AsyncArgs,
  createOrderV4Async,
  type CreateOrderV4AsyncResponse
} from "../requests";

import { toV4Basket } from "./to-v4-basket";

type CreateOrderKioskArgs = {
  paymentMethod: "card" | "credit" | "swish" | "billing";
  paidWithSaldoUnit: CreateOrderV3Args["paidWithSaldoUnit"];
  serviceId: CreateOrderV3Args["serviceId"];
  userId?: CreateOrderV3Args["userId"];
  childId?: CreateOrderV3Args["childId"];
  groupName?: CreateOrderV3Args["groupName"];
};

type CreateOrderKioskArgsV4 = Omit<CreateOrderKioskArgs, "serviceId"> & {
  paymentId: string;
};

const usePrepareOrderArgs = () => {
  const { os, osVersion } = useDeviceData();
  const till = useKioskStore((state) => state.till);
  const orderId = useKioskSession((session) => session?.orderId);
  const childId = useKioskSession((session) => session?.childId ?? null);
  const { productsOrdering, unitTotalPayableInclVat } = useKioskProducts();
  const diningOption = useKioskDiningOption();
  const shopperName = useKioskShopperName();
  const postCheckoutTextInputs = useKioskSession(
    (session) => session?.postCheckoutTextInputs
  );
  const { data: locationGroupNames } = useCurrentSchool(
    (school) => school.groupNames
  );

  const provider = till?.paymentGatewayConfig?.provider;

  // The kiosk session's childId, or else the guest group, never the employee
  // store: it is POS-scoped and outlives kiosk sessions.
  const userIdentifier = getKioskUserIdentifier(
    till,
    locationGroupNames,
    childId
  );

  // The terminal is charged what the customer agreed to, deposits included;
  // the server recomputes the same figure from the basket we declare.
  const totalPriceInclVat = minorUnitToMajorUnit(unitTotalPayableInclVat);

  const createOrderArgs = (
    args: CreateOrderKioskArgs,
    isFreedomPay = false
  ) => {
    if (!orderId) {
      throw new Error("Session is not active");
    }

    const auth = getAuth();
    if (!isProfileAuth(auth)) {
      throw new DisplayError(DisplayErrorCodeEnum.PROFILE_NOT_SELECTED);
    }

    const {
      tillId,
      moduleId,
      schoolId,
      childId: posChildId,
      profileId,
      userId: posUserId,
      partnerId
    } = auth;

    // card and swish payments both run on the terminal, ensure it is connected
    if (
      !isFreedomPay &&
      !till?.terminalId &&
      (args.paymentMethod === "card" || args.paymentMethod === "swish")
    ) {
      throw new Error("Terminal is not connected");
    }

    if (!till.sessionId) {
      throw new Error("Till is not open");
    }

    if (!os || !osVersion) {
      throw new Error("OS or OS Version not provided");
    }

    const diningInput = diningOption
      ? { [diningOption.inputId]: diningOption.value }
      : {};
    const postCheckoutInputs = postCheckoutTextInputs || {};

    const textInputData = {
      ...diningInput,
      ...postCheckoutInputs
    };

    const info =
      Object.keys(textInputData).length > 0
        ? { textInput: textInputData }
        : undefined;

    // `null` here is the name never arriving, not the guest clearing the field
    // (that writes ""), so the kitchen ticket is nameless for a guest we know.
    if (childId && shopperName === null && kioskUsesCustomShopperName(till)) {
      logger.warn("[Kiosk] Identified guest is ordering without a name", {
        order_id: orderId,
        till_id: till.id
      });
    }

    return {
      ...args,
      orderId,
      tillId,
      moduleId,
      schoolId,
      userId: args.userId ?? posUserId,
      childId: args.childId ?? posChildId,
      groupName:
        args.groupName ??
        (userIdentifier?.type === "group-name"
          ? userIdentifier.value
          : undefined),
      profileId,
      partnerId,
      sessionId: till.sessionId,
      deviceInfo: { os, osVersion },
      customShopperName: shopperName ?? undefined,
      info,
      basket: productsOrdering
    };
  };

  const createOrderV4Args = (
    args: CreateOrderKioskArgsV4
  ): CreateOrderV4AsyncArgs => {
    const baseArgs = createOrderArgs(
      { ...args, serviceId: "not-needed" },
      true
    );

    if (!till.freedompayTerminalId) {
      throw new Error("No terminal id is associated with this till");
    }

    // Transform info object for V4 API format
    const diningInput = diningOption
      ? { [diningOption.inputId]: diningOption.value }
      : {};
    const postCheckoutInputs = postCheckoutTextInputs || {};

    const textInputData = {
      ...diningInput,
      ...postCheckoutInputs
    };

    const textInputs =
      Object.keys(textInputData).length > 0
        ? Object.entries(textInputData).map(([id, value]) => ({ id, value }))
        : undefined;

    const info =
      textInputs || shopperName
        ? {
            customShopperName: shopperName ?? undefined,
            textInputs
          }
        : undefined;

    return {
      ...baseArgs,
      basket: toV4Basket(productsOrdering),
      payments: [
        {
          paymentId: args.paymentId,
          paymentMethod: "card",
          unitRequestedAmountInclVat: unitTotalPayableInclVat
        }
      ],
      requestId: baseArgs.orderId,
      userIdentifier,
      info
    };
  };

  return {
    createOrderArgs,
    createOrderV4Args,
    provider,
    tillId: till.id,
    orderId,
    totalPriceInclVat
  };
};

type UseFreedomPayOrderArgs = {
  createOrderAsync: () => Promise<CreateOrderV4AsyncResponse | undefined>;
  orderId: string;
  paymentId: string;
  totalPriceInclVat: number;
  taxTotal: number;
  items: TransactionItem[];
};

export const CREATE_ORDER_V4_MUTATION_KEY = "create-order-v4";

type FreedompayCreateOrderResult = {
  requestId: string;
  paymentId: string;
};

const FREEDOMPAY_ORDER_FLOW_MUTATION_KEY = "freedompay-order-flow";

export function useFreedomPayOrder() {
  const { mutateAsync: voidPoiPayment } = useVoidPoiPayment();
  const { mutateAsync: startPoiPayment } = useStartPoiPayment();

  const { mutateAsync: acknowledgeOrderPaymentV4 } =
    useAcknowledgeOrderPaymentV4();

  // Expose a mutation that orchestrates the full async flow
  return useMutation<
    FreedompayCreateOrderResult,
    Error,
    UseFreedomPayOrderArgs
  >({
    mutationKey: [FREEDOMPAY_ORDER_FLOW_MUTATION_KEY],
    mutationFn: async ({
      createOrderAsync,
      orderId,
      paymentId,
      totalPriceInclVat,
      taxTotal,
      items
    }) => {
      const { tillId } = getAuth();

      if (!tillId) throw new Error("Till ID is not available");

      const poiPromise = startPoiPayment({
        orderId,
        amount: totalPriceInclVat,
        paymentId,
        items,
        taxTotal
      });
      const orderPromise = createOrderAsync();

      logger.debug("FreedomPay: waiting for POI and order to settle", {
        order_id: orderId,
        payment_id: paymentId,
        amount: totalPriceInclVat,
        event: "freedompay_sale_mrc_in_flight"
      });
      const [poiSettled, orderSettled] = await Promise.allSettled([
        poiPromise,
        orderPromise
      ]);

      logger.debug("FreedomPay: POI and order settled", {
        order_id: orderId,
        amount: totalPriceInclVat,
        poi_settled: poiSettled.status,
        order_settled: orderSettled.status
      });

      const poiOk = poiSettled.status === "fulfilled";
      const orderOk = orderSettled.status === "fulfilled";

      if (poiOk && orderOk) {
        const requestId = poiSettled.value.requestId;
        const isPaymentCreated = orderSettled.value?.payments?.some(
          (payment) => payment.paymentId === paymentId
        );

        if (!isPaymentCreated) {
          logger.error(
            new Error("Missing paymentId"),
            "FreedomPay: Missing paymentId",
            {
              order_id: orderId,
              amount: totalPriceInclVat,
              payment_id: paymentId
            }
          );

          throw new Error("Missing paymentId after POI payment settled");
        }

        await acknowledgeOrderPaymentV4({
          tillId,
          orderId,
          paymentId,
          status: "paid",
          transactionReference: requestId
        });

        return { requestId, paymentId };
      }

      // Mismatch handling
      // POI success but order failed → void POI
      if (poiOk && !orderOk) {
        try {
          logger.debug("FreedomPay: voiding POI payment (reusing sale MRC)", {
            request_id: poiSettled.value.requestId,
            order_id: orderId,
            payment_id: paymentId,
            event: "freedompay_recovery_void_mrc"
          });
          await voidPoiPayment({
            requestId: poiSettled.value.requestId,
            orderId,
            paymentId
          });
        } catch (error) {
          logger.error(error, "FreedomPay: Error voiding POI payment");
        }
        // surface original error
        throw orderSettled.reason;
      }

      // Order success but POI failed → cancel backend order
      if (!poiOk && orderOk) {
        if (
          isDisplayError(poiSettled.reason) &&
          poiSettled.reason.code === DisplayErrorCodeEnum.POI_TIMEOUT
        ) {
          logger.error(poiSettled.reason, "FreedomPay: POI payment timed out", {
            order_id: orderId,
            payment_id: paymentId,
            amount: totalPriceInclVat,
            timeout_ms: POI_TIMEOUT_MS,
            event: "freedompay_poi_timeout"
          });
        }
        const isPaymentCreated = orderSettled.value?.payments?.some(
          (payment) => payment.paymentId === paymentId
        );

        if (isPaymentCreated) {
          try {
            logger.debug(
              "FreedomPay: cancelling order after poi payment failed",
              {
                order_id: orderId,
                amount: totalPriceInclVat,
                payment_id: paymentId
              }
            );
            await acknowledgeOrderPaymentV4({
              tillId,
              orderId,
              paymentId,
              status: "cancelled",
              transactionReference: "N/A"
            });
          } catch (error) {
            logger.error(
              error,
              "FreedomPay: Error cancelling order after poi payment failed"
            );
          }
        }

        throw poiSettled.reason;
      }

      // Both failed
      throw new Error("FreedomPay flow failed: both POI and order failed");
    },
    onError: (error, variables) => {
      logger.error(error, "FreedomPay: Error creating order flow", {
        order_id: variables.orderId,
        payment_id: variables.paymentId,
        amount: variables.totalPriceInclVat,
        is_timeout:
          isDisplayError(error) &&
          error.code === DisplayErrorCodeEnum.POI_TIMEOUT
      });
    }
  });
}

type FreedomPayOrderResponse =
  | {
      status: "idle";
      payload?: never;
    }
  | {
      status: "pending-poi-payment";
      payload?: never;
    }
  | {
      status: "pending-order-creation";
      payload?: never;
    }
  | {
      status: "pending-signature-check";
      payload: {
        requestId: string;
        requireSignatureCheck: boolean;
        receiptList: string[];
      };
    }
  | {
      status: "success";
      payload: {
        requestId: string;
        requireSignatureCheck: boolean;
        receiptList: string[];
      };
    }
  | {
      status: "error";
      payload: {
        error: Error;
      };
    };

export function useFreedomPayOrderStatus(): FreedomPayOrderResponse {
  const orderMutation = useLatestMutation<FreedompayCreateOrderResult>({
    key: [CREATE_ORDER_V4_MUTATION_KEY]
  });

  const freedompayOrderMutation = useLatestMutation({
    key: [FREEDOMPAY_ORDER_FLOW_MUTATION_KEY]
  });

  const poiMutation = useLatestStartPoiPayment();

  return useMemo(() => {
    if (!poiMutation && !freedompayOrderMutation && !orderMutation) {
      return { status: "idle" };
    }

    if (poiMutation?.status === "pending") {
      return { status: "pending-poi-payment" };
    }

    if (freedompayOrderMutation?.status === "pending") {
      return { status: "pending-order-creation" };
    }

    if (poiMutation?.data?.requireSignatureCheck) {
      return {
        status: "pending-signature-check",
        payload: poiMutation.data
      };
    }

    if (
      orderMutation?.status === "success" &&
      poiMutation?.status === "success" &&
      poiMutation.data?.requestId
    ) {
      return {
        status: "success",
        payload: poiMutation.data
      };
    }

    if (orderMutation?.status === "error") {
      return {
        status: "error",
        payload: { error: orderMutation.error ?? new Error("Unknown error") }
      };
    }

    if (poiMutation?.status === "error") {
      return {
        status: "error",
        payload: { error: poiMutation.error ?? new Error("Unknown error") }
      };
    }

    return { status: "pending-order-creation" };
  }, [poiMutation, orderMutation, freedompayOrderMutation]);
}

export function useCreateOrderAsyncV4() {
  return useMutation({
    mutationFn: (args: CreateOrderV4AsyncArgs) => createOrderV4Async(args)
  });
}

export function useCreateOrderKioskV4() {
  const { createOrderV4Args, tillId } = usePrepareOrderArgs();

  return useMutation({
    mutationKey: [CREATE_ORDER_V4_MUTATION_KEY],
    mutationFn: async (args: CreateOrderKioskArgsV4) => {
      if (args.paymentMethod === "card") {
        return createOrderV4Async({ ...createOrderV4Args(args), tillId });
      }
    }
  });
}

export function useAcknowledgeOrderPaymentV4() {
  return useMutation({
    mutationFn: (args: AcknowledgeOrderPaymentV4Args) =>
      acknowledgeOrderPaymentV4(args)
  });
}

export function useCreateOrderKiosk() {
  const { createOrderArgs, orderId } = usePrepareOrderArgs();

  return useMutation({
    mutationKey: [CURRENT_ORDER_MUTATION_KEY, orderId],
    mutationFn: async (args: CreateOrderKioskArgs) => {
      if (args.paymentMethod === "card" || args.paymentMethod === "swish") {
        return createOrderV3(createOrderArgs(args));
      }

      return createOrderV1(createOrderArgs(args));
    }
  });
}

const CURRENT_ORDER_MUTATION_KEY = "create-order-kiosk";

export function useCurrentOrderMutation() {
  const orderId = useKioskSession((session) => session?.orderId);
  const serviceId = useKioskSession((session) => session?.serviceId);

  const mutations = useMutationState({
    filters: {
      mutationKey: [CURRENT_ORDER_MUTATION_KEY, orderId],
      predicate(mutation: MutationType<typeof useCreateOrderKiosk>) {
        return mutation.state.variables?.serviceId === serviceId;
      }
    },
    select: (mutation) =>
      mutation.state as MutationState<unknown, Error, CreateOrderKioskArgs>
  });

  return mutations[mutations.length - 1];
}
